/**
 * @fileoverview Column Visibility Toggle
 *
 * Dropdown button that lets users show/hide table columns.
 * Persists visibility state to localStorage.
 */

import { Columns } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { ColumnDefinition } from "../types/ColumnConfig";

export interface ColumnVisibilityToggleProps {
  /** All column definitions from the table config */
  columns: ColumnDefinition[];
  /** Set of column IDs that are currently hidden */
  hiddenColumns: Set<string>;
  /** Callback when visibility changes */
  onHiddenColumnsChange: (hiddenColumns: Set<string>) => void;
}

export const ColumnVisibilityToggle = ({
  columns,
  hiddenColumns,
  onHiddenColumnsChange,
}: ColumnVisibilityToggleProps) => {
  const toggleableColumns = columns.filter(
    (col) => col.toggleable !== false && col.id !== "checkbox"
  );

  const handleToggle = (columnId: string) => {
    const next = new Set(hiddenColumns);
    if (next.has(columnId)) {
      next.delete(columnId);
    } else {
      next.add(columnId);
    }
    onHiddenColumnsChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center justify-center rounded-md border border-neutral-primary/20 bg-white p-2 text-neutral-primary hover:bg-neutral-primary/5 transition-colors"
          title="Kolommen tonen/verbergen"
        >
          <Columns size={18} weight="duotone" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="w-56 max-h-[400px] overflow-y-auto p-2"
      >
        <div className="flex flex-col gap-0.5">
          {toggleableColumns.map((col) => {
            const isVisible = !hiddenColumns.has(col.id);
            return (
              <label
                key={col.id}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-neutral-primary/5 transition-colors"
              >
                <Checkbox
                  checked={isVisible}
                  onCheckedChange={() => handleToggle(col.id)}
                />
                <span className="text-neutral-primary">
                  {typeof col.label === "string" ? col.label : col.id}
                </span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
